import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import merge from 'lodash/merge'

import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { ConfigLayerWFSOptions } from './ConfigLayerWFS'
import type { ConfigLayerWMSOptions } from './ConfigLayerWMS'
import type { ServerType } from './ServerType'
import type { ConfigLayerOptions } from '../ConfigLayer'

import * as PoziUtils from 'api/utils'
import { StyleGeoStyler } from 'config/catalog/StyleGeoStyler'

import { ConfigLayerCapability } from './ConfigLayerCapability'
import { ConfigLayerCatalog } from './ConfigLayerCatalog'
import { ConfigLayerWMS } from './ConfigLayerWMS'
import QGISProjectSettingsParser from './QGISProjectSettingsParser'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerQGISProjectSettingsOptions extends ConfigLayerOptions {
  type: ConfigLayerType.QGIS_PROJECT_SETTINGS
  serverType?: ServerType
  layers: ConfigLayerWMSOptions[]
}

export class ConfigLayerQGISProjectSettings extends ConfigLayerCatalog {
  static async fetchCapability (capabilityOptions: ConfigLayerCapabilityOptions) {
    const url = PoziUtils.getLayerUrl(capabilityOptions)
    if (url) {
      try {
        const text = await ConfigLayerCapability.fetchUrl(url)
        if (text) {
          const wmsCapability = this.getCapabilities(text)
          if (wmsCapability) {
            // console.debug('ConfigLayerQGISProjectSettings - fetchCapability - layers', capabilityOptions.titleid, wmsCapability)

            const keywords = this.getKeywords(wmsCapability)

            // console.debug('ConfigLayerQGISProjectSettings - fetchCapability - keywords', capabilityOptions.titleid, keywords)

            const configFromKeywords = ConfigLayerCapability.getConfigFromKeywords(keywords)

            // console.debug('ConfigLayerQGISProjectSettings - fetchCapability - configFromKeywords', capabilityOptions.titleid, configFromKeywords)

            const { profiles } = configFromKeywords

            // console.debug('ConfigLayerQGISProjectSettings - fetchCapability - profiles', capabilityOptions.titleid, profiles)

            // Store profiles in configuration. Keyword based profiles override the ConfigManager ones
            capabilityOptions.profiles = profiles || capabilityOptions.profiles

            const ConfigLayerQGISProjectSettingsOptions: ConfigLayerQGISProjectSettingsOptions = {
              type: ConfigLayerType.QGIS_PROJECT_SETTINGS,
              serverType: 'qgis',
              title: capabilityOptions.title,
              titleid: capabilityOptions.titleid,
              group: capabilityOptions.group,
              groupid: capabilityOptions.groupid,
              layers: this.getLayers(capabilityOptions, wmsCapability)
            }

            // console.debug('fetchCapability', capabilityOptions.titleid, ConfigLayerQGISProjectSettingsOptions)

            return ConfigLayerQGISProjectSettingsOptions
          }
        }
      } catch (error) {
        console.error('Error fetching capabilities: ', url, error)
      }
    } else {
      console.error('Error getting url property from config to request QGISProjectSettings: ', capabilityOptions)
    }
  }

  private static getCapabilities (text: string) {
    const parser = new QGISProjectSettingsParser()
    const wmsCapability: QGISProjectSettingsCapability = parser.read(text)
    return wmsCapability
  }

  private static getChildLayers (
    capabilityOptions: ConfigLayerCapabilityOptions,
    wmsCapability: QGISProjectSettingsCapability,
    layers: WMSLayer[],
    // layerPath: string[] = []
    layerPathArray: string[] = [capabilityOptions.title],
    parentConf?: ConfigLayerOptions
  ) {
    const configObjs: ConfigLayerWMSOptions[] = []
    const wfsLayers = ConfigLayerQGISProjectSettings.getWfsLayers(capabilityOptions, wmsCapability)

    // For layer filtering below
    const hasLayerFilter = this.hasLayerFilter(capabilityOptions)
    const layerFilterArray = this.getLayerFilterArray(capabilityOptions)

    // console.debug('getChildLayers', capabilityOptions.titleid, parentConf?.titleid, layers)

    layers.forEach((layer, index: number) => {
      const layerTitle = this.getLayerTitle(layer)
      const layerNameOrTitle = this.getLayerName(layer) || layerTitle

      const hasChildLayers = !!layer.Layer

      let configLayer: ConfigLayerWMSOptions | ConfigLayerWFSOptions
      const configLayerWMS = ConfigLayerWMS.getLayer(capabilityOptions, wmsCapability, layer, index)

      // It is possible to override the layer type with the 'type' keyword. This can be used
      // to force the layer to be ImageWMS or TileWMS for example, even though it's available as WFS.
      // We will only accept supported types to avoid surprises.
      let layerTypeFromKeywords = configLayerWMS.configFromKeywords?.type
      if (layerTypeFromKeywords) {
        const supportedCustomTypes = ['ImageWMS', 'TileWMS']

        // Get the matching type from the list of supported types (or undefined if not supported)
        layerTypeFromKeywords = supportedCustomTypes.find(type => type.toLowerCase() === layerTypeFromKeywords.toLowerCase())

        if (!layerTypeFromKeywords) {
          console.log(`ConfigLayerQGISProjectSettings - getChildLayers - layer type from keywords '${layerTypeFromKeywords}' of layer '${layerNameOrTitle}' is not supported. Using default type.`)
        }
      }

      // A layer is deemed to be WFS if it's available as WFS and if the type is not overridden
      const layerIsWfs = wfsLayers.includes(layerNameOrTitle) && !layerTypeFromKeywords

      // Explicitly set the serverType to QGIS
      configLayerWMS.serverType = 'qgis'

      // Do not add layers marked as `enabled: false` (usually from keywords)
      const layerEnabled = configLayerWMS.enabled ?? true
      if (!layerEnabled) {
        console.debug(`ConfigLayerQGISProjectSettings - getChildLayers - layer is not enabled: '${configLayerWMS.titleid}' (${configLayerWMS.type}). Not adding.`)
        return
      }

      // QGIS provides attribute information like whether there are aliases
      const attributes: Record<string, any> = this.getFeatureAttributes(layer)
      if (!isEmpty(attributes)) {
        // Store attributes info in the layer config. This will be used to determine
        // field aliases elsewhere in the code for example.
        configLayerWMS.attributes = attributes

        // Force ordering of feature attributes since QGIS sorts them alphabetically in GeoJSON responses
        // TODO: add qgis github issue here when submitted
        const orderFields = Object.keys(attributes)

        // Add reordering transformer to the layer
        configLayerWMS.config.transformer = { orderFields }
      }

      // Convert WMS config object to WFS (actually a GeoJSON) config object
      // TODO: move this to ConfigLayerWFS as something like 'getLayerFromWMSLayer'
      if (layerIsWfs && !hasChildLayers) {
        // console.debug('QGIS getChildLayers - layer is WFSLayer', configLayerWMS.titleid, configLayerWMS)

        // WFS doesn't like spaces in typenames, nor colons if they're not part of a namespace.
        // Since there is no direct link between a WMS layer name and its corresponding WFS name,
        // as a bit of a hack, we convert the name to hopefully what QGIS Server expects
        const typeName = PoziUtils.getWfsTypeNameFromTitle(layerNameOrTitle)

        // Special case: if the parent has been set to to 'Whats here' in the WMS layer, we
        // will reset it here to avoid all WFS layers showing up in the details panel.
        // Note: this is a hack and should not be needed
        //
        // TODO: remove the need for this hack.
        if (configLayerWMS.parent === 'Whats here') {
          configLayerWMS.parent = ''
        }

        // Remove any WMS legend from the WMS config so we can render our
        // GeoStyler legend.
        // We do this before merging with the keywords config, so that we can
        // override this behaviour and allow for a user set legend URL.
        configLayerWMS.legend = ''

        let styleGeoStyler

        // console.debug('QGIS getChildLayers - layer', configLayerWMS.titleid, configLayerWMS)

        // Use styleUrl from config, if provided and use the capabilities URL as the base
        // if the styleUrl is a relative one.
        if (configLayerWMS.styleUrl) {
          try {
            const capabilitiesUrl = capabilityOptions.config?.spatial?.url
            const url = (new URL(configLayerWMS.styleUrl, capabilitiesUrl)).href

            styleGeoStyler = {
              url,
              type: 'SLD'
            }
          } catch (error) {
            console.debug('QGIS getChildLayers - invalid styleUrl', configLayerWMS.titleid, error, configLayerWMS)
          }
        }

        // Otherwise, try to automatically derive it
        if (!styleGeoStyler) {
          // console.debug('QGIS getChildLayers - try deriving geostyler style automatically', layerNameOrTitle, capabilityOptions.titleid)

          styleGeoStyler = StyleGeoStyler.getStyleGeoStyler(capabilityOptions, layerNameOrTitle)
        }

        // If a projection has been defined, we will automatically request the WFS information in
        // that projection in GML instead of GeoJSON (as QGIS does only deliver GeoJSON in EPSG:4326)
        const projection = configLayerWMS.config?.projection

        // If the catalogue has been marked as static, we'll derive the path to the data
        // from the catalogue name (folder), layer title (file base name) and loader (file extension)
        const isStatic = !!capabilityOptions.static

        const url = configLayerWMS.config.url

        // TODO: work out how to handle the loader part
        // @ts-expect-error
        let loader = configLayerWMS.loader || (projection ? 'gml' : 'geojson')

        // Try to automatically derive loader from URL but only when isStatic is set
        if (isStatic) {
          const extension = (url.match(/.(\w+)$/))?.[1] // e.g. matches .json, .csv, etc.
          console.debug('QGIS WFS loader', extension, loader)

          switch (extension) {
            case 'fgb':
              loader = 'flatgeobuf'
              break

            case 'geojson':
            case 'json':
            default:
              loader = 'geojson'
          }
        }

        const params = isStatic
          ? {}
          : {
              // pre-set some params that can be overriden
              TYPENAME: typeName, // handy in case we cannot derive typename from WMS layer name

              ...configLayerWMS.config.params, // get params from config.params

              // and hardcode the following params
              SERVICE: 'WFS',
              REQUEST: 'GetFeature',
              VERSION: '1.1.0',
              SRSNAME: projection || 'EPSG:4326',
              OUTPUTFORMAT: projection ? 'GML3' : 'application/json'
            }

        const wfsObj = {
          type: 'GeoJSON',
          config: {
            spatial: {
              // For some reason, WMS layers have url in config.url
              url,

              label: configLayerWMS.config.spatial.label,
              loader,
              params,
              proxyUrl: configLayerWMS.config.proxyUrl
            },
            transformer: configLayerWMS.config.transformer
          },
          // showInLayerControl: false, // This will be managed by the layer tree instead
          styleGeoStyler
        }

        // Deep merge the two configs into one
        configLayer = merge({},
          configLayerWMS,
          wfsObj
        )

        // console.debug('QGIS getChildLayers - conversion from WMS to GeoJSON layer', configLayer.titleid, configLayer)
      } else {
        configLayer = configLayerWMS

        // console.debug('QGIS getChildLayers - layer is WMS', configLayer.titleid, configLayer)
      }

      // Keep track of parent-child relationship through titleid
      // Note: we cannot use a direct reference to the parent object due to
      //       immer only allowing unidirectional trees:
      //       https://immerjs.github.io/immer/pitfalls/#immer-only-supports-unidirectional-trees
      if (parentConf) {
        configLayer.parentid = parentConf.titleid
      }

      // Layer title 'breadcrumb'
      const childLayerPathArray = [...layerPathArray, configLayer.title]
      configLayer.layerPathArray = childLayerPathArray

      // Create and set child layers
      const childLayers = layer.Layer || []
      if (childLayers.length > 0) {
        // console.debug('getLayers - layer has children. Recursing.', configLayer.titleid, configLayer, layer.Layer)
        configLayer.layers = ConfigLayerQGISProjectSettings.getChildLayers(capabilityOptions, wmsCapability, childLayers, childLayerPathArray, configLayer)
      }

      if (configLayer.layers) {
        // If we encounter an empty layer group, ignore this layer completely
        if (configLayer.layers.length === 0) {
          return
        }

        // If none of the children in this group should be shown in the layer control, do not show the group itself
        // in the layer control either
        const showInLayerControl = configLayer.layers.some(layerConf => layerConf.showInLayerControl)

        // console.debug('ConfigLayerQGISProjectSettings - getLayers -showInLayerControl?', configLayer.titleid, showInLayerControl, cloneDeep(configLayer))

        if (!showInLayerControl) {
          // console.debug('ConfigLayerQGISProjectSettings - getLayers - no child layers are shown in layer control. Not showing group either.', configLayer.titleid, configLayer)

          configLayer.showInLayerControl = false
        }
      }

      // Leaf node. Determine whether to show in layer control or not:
      // If the layer URL has a filter in its params, we do not want to show it in the layer control.
      // This is a remnant of the old child layer behaviour in Pozi and may be removed in the future.
      // We also don't want to show the layer if it has no geometry.
      // The user can override this behaviour with the showInLayerControl keyword
      if (!configLayer.layers) {
        const showInLayerControlFromKeywords = configLayer.configFromKeywords?.showInLayerControl
        const showInLayerControlIsSetFromKeywords = !isNil(showInLayerControlFromKeywords)

        const layerUrl = PoziUtils.getLayerUrl(configLayer)
        const urlParamsObj = PoziUtils.getUrlParamsObj(layerUrl)

        const hasFilterParam = urlParamsObj && Object.keys(urlParamsObj).some(key => !!key.match(/filter/i))
        const layerHasNoGeometry = configLayer.geometryType === 'NoGeometry'

        // Special treatment for layers with a filter param set or layers without a geometry
        if (hasFilterParam || layerHasNoGeometry) {
          // Do not override the showInLayerControl if it was already set in the keywords
          if (showInLayerControlIsSetFromKeywords) {
            // console.debug('getChildLayers - showInLayerControl value is set in keywords. Not overriding', configLayer.titleid, showInLayerControlFromKeywords, layerHasNoGeometry)
          } else if (configLayer.showInLayerControl) {
            // console.debug('getChildLayers - showInLayerControl was not set in keywords and will now be set to false due to it being a special child layer', configLayer.titleid, showInLayerControlFromKeywords, layerHasNoGeometry)

            configLayer.showInLayerControl = false
          }
        }
      }

      // We are at a leaf node. Determine whether this layer matches any provided layer filter and ignore if
      // there is no match
      if (childLayers.length === 0 && hasLayerFilter) {
        const isMatch = this.layerMatchesFilter(configLayer, layerFilterArray)
        if (!isMatch) {
          console.debug(`ConfigLayerQGISProjectSettings - getLayers - ignoring layer '${configLayer.title}' due to it not matching filter '${capabilityOptions.layerFilter}'`)

          return
        }
      }

      // console.debug('QGIS getChildLayers - push layer', configLayer.titleid, configLayer.type, configLayer)

      // If a catalogue is configured to be flattened, we will add all
      // child leaf layers individually instead of a tree structure
      const flatten = capabilityOptions.flatten
      if (flatten) {
        // console.debug('flatten', configLayer.titleid, flatten, !!configLayer.layers, configLayer, capabilityOptions)

        // Add group to flattened layers so it will be picked up for rendering
        configLayer.group = capabilityOptions.group
        configLayer.groupid = capabilityOptions.groupid

        // Do the same for all its child layers
        if (configLayer.layers) {
          for (const childLayerConf of configLayer.layers) {
            // Add group to flattened child layers so it will be picked up for rendering
            childLayerConf.group = capabilityOptions.group
            childLayerConf.groupid = capabilityOptions.groupid

            configObjs.push(childLayerConf)
          }
        } else {
          configObjs.push(configLayer)
        }
      } else {
        configObjs.push(configLayer)
      }
    })

    return configObjs
  }

  static getFeatureAttributes (layer: any) {
    const attributesArr = layer?.Attributes?.Attribute || []

    // Convert array into object using attribute name as the key
    const attributesObj: Record<string, any> = {}
    for (const attribute of attributesArr) {
      attributesObj[attribute.name] = attribute
    }

    // console.debug('QGIS getFeatureAttributes', attributesObj)

    return attributesObj
  }

  static getKeywords (wmsCapability: QGISProjectSettingsCapability) {
    const keywordsRaw = wmsCapability?.Service?.KeywordList?.Keyword || []

    // Handle special case where keyword is an object with some attribute(s) like 'vocabulary' and
    // a special field called '#text', which is the value we need
    //
    // TODO: change fast-xml-parser settings to avoid this
    const keywords = keywordsRaw.map(keyword => typeof keyword === 'object' ? keyword['#text'] : keyword)

    // console.debug('getKeywords', wmsCapability?.Service?.Title, keywords)

    return keywords
  }

  static getLayerName (layer: any) {
    // Force value as string (in case of a layer name like the number 2019)
    // This is because the fast-xml-parser returns a number instead of a string if the value
    // looks like a number
    const layerName = String(layer?.Name ?? '')

    // console.debug('getLayerName', layerName)

    return layerName
  }

  static getLayerTitle (layer: any) {
    // Force value as string (in case of a layer title like the number 2019)
    // This is because the fast-xml-parser returns a number instead of a string if the value
    // looks like a number
    const layerTitle = String(layer?.Title ?? '')

    // console.debug('getLayerTitle', layerTitle)

    return layerTitle
  }

  private static getLayers (capabilityOptions: ConfigLayerCapabilityOptions, wmsCapability: QGISProjectSettingsCapability) {
    const layers = wmsCapability.Capability?.Layer?.Layer || []

    let configObjs = ConfigLayerQGISProjectSettings.getChildLayers(capabilityOptions, wmsCapability, layers)

    configObjs = this.postProcessLayers(capabilityOptions, configObjs)

    // console.debug('QGIS getLayers - configObjs', configObjs)

    return configObjs
  }

  private static getWfsLayers (capabilityOptions: ConfigLayerCapabilityOptions, wmsCapability: QGISProjectSettingsCapability) {
    let wfsLayers = wmsCapability?.Capability?.WFSLayers?.WFSLayer || []

    // Extract names from objects
    wfsLayers = wfsLayers.map((obj: any) => obj.name)

    // console.debug('QGIS getWfsLayers', wfsLayers)

    return wfsLayers
  }
}

export interface WMSLayer {
  Name: string
  Title: string
  Style: any[]
  Abstract: string
  Layer?: Array<WMSLayer>
  opacity: number
  queryable: boolean
}

export interface QGISProjectSettingsCapability {
  Service: {
    ContactInformation: {
      ContactPersonPrimary: {
        ContactOrganization: string
      }
    }
    KeywordList?: {
      Keyword?: [string| Record<string, string>]
    }
    Title?: string
  }
  Capability: {
    Request: {
      GetMap: {
        DCPType: Array<{ HTTP: { Get: { OnlineResource: string } } }>
      }
    }
    Layer: { Layer: Array<WMSLayer> }
    WFSLayers: { WFSLayer: Array<string> }
  }
}
